<template>
    <div class="grant-agreement" v-if="MonitoringForm.initialized">
        <div class="grant-agreement-box row" v-if="!showContractDetails">
            <div class="col-6">
                <h3>{{ $t('monitoring.title__grant_agreement') }} - {{ MonitoringForm.reference }}</h3>

                <p v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"><b>{{ $t('monitoring.field_title__number_of_project') }}</b>: <input type="text" v-model="MonitoringForm.model.project_number" /></p>
                <p v-else><b>{{ $t('monitoring.field_title__number_of_project') }}</b>: {{ MonitoringForm.model.project_number }}</p>
                <p><b>{{ $t('monitoring.field_title__name_of_organisation') }}</b>: {{ MonitoringForm.organisation }}</p>
                <p v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"><b>{{ $t('monitoring.field_title__bank_account') }}</b>: <input type="text" v-model="MonitoringForm.model.bank_account" /></p>
                <p v-else><b>{{ $t('monitoring.field_title__bank_account') }}</b>: {{ MonitoringForm.model.bank_account }}</p>
            </div>
            <div class="col-6">
                <base-button
                    type="primary"
                    class="pull-right"
                    @click.native="showContractDetails = true"
                    >
                    {{ $t('monitoring.button__view_contract_details') }}
                </base-button>
            </div>
        </div>
        <div v-else>
            <div class="grant-agreement-box row">
                <div class="col-6">
                    <h3 class="mt-2 mb-0">{{ $t('monitoring.title__grant_agreement') }} - {{ MonitoringForm.reference }}</h3>
                </div>
                <div class="col-6">
                    <base-button
                        type="primary"
                        class="pull-right"
                        @click="viewOriginalProposal"
                        >
                        {{ $t('monitoring.button__view_original_proposal') }}
                    </base-button>
                </div>
            </div>
            <card class="mt-5">
                <div class="row">
                    <div class="col-lg-4">
                        <!-- Title of the project -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('monitoring.field_title_project_title') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.project_title"
                                v-model="MonitoringForm.title"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :placeholder="$t('monitoring.field_title_project_title')"
                                >
                            </textarea>
                        </div>

                        <!-- Region -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('medium_grant.technical__1_region') }}</h4>
                            <!-- <label for="regionSelect">{{ $t('medium_grant.technical__1_region') }}</label> -->
                            <select
                                class="form-control"
                                @change="setRegion"
                                v-model="MonitoringForm.model.region_id"
                                name="grant_agreement.region"
                                id="regionSelect"
                                v-validate="'required'"
                                :data-vv-as="$t('medium_grant.technical__1_region')"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                >
                                <option value="" selected="selected">{{ $t('medium_grant.general__please_select') }}</option>
                                <option :value="region.regionId" :selected="MonitoringForm.regionId === region.regionId" v-for="region in regions" :key="region.regionId">{{region.code}} - {{region.name}}</option>
                            </select>
                        </div>

                        <!-- ACP Country(ies) -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('medium_grant.technical__1_acp_country') }}</h4>
                            <multiselect
                                name="grant_agreement.acp_country"
                                v-model="MonitoringForm.model.grant_agreement.acp_country"
                                selectLabel=""
                                selectedLabel=""
                                deselectLabel="Clear"
                                v-validate="'required'"
                                label="name"
                                track-by="countryId"
                                :data-vv-as="$t('medium_grant.technical__1_acp_country')"
                                :tag-placeholder="$t('medium_grant.general__please_select')"
                                :placeholder="$t('medium_grant.general__please_select')"
                                :options="countriesByRegion"
                                :multiple="true"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                >
                            </multiselect>
                        </div>

                        <!-- Priority Landscape Conservation targeted -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('medium_grant.technical__1_priority_landscape') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.priority_landscape"
                                v-model="MonitoringForm.model.grant_agreement.priority_landscape"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :error="getError('grant_agreement.priority_landscape')"
                                :data-vv-as="$t('medium_grant.technical__1_priority_landscape')"
                                :label="$t('medium_grant.technical__1_priority_landscape')"
                                :placeholder="$t('medium_grant.technical__1_priority_landscape')"
                                >
                            </textarea>
                        </div>

                        <!-- PA(s) targeted -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('medium_grant.technical__1_pa_target') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.pa_target"
                                v-model="MonitoringForm.model.grant_agreement.pa_target"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :error="getError('grant_agreement.pa_target')"
                                :data-vv-as="$t('medium_grant.technical__1_pa_target')"
                                :label="$t('medium_grant.technical__1_pa_target')"
                                :placeholder="$t('medium_grant.technical__1_pa_target')"
                                >
                            </textarea>
                        </div>

                        <!-- Name of lead organisation -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('medium_grant.technical__1_name_lead_org') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.name_of_lead_org"
                                v-model="MonitoringForm.model.grant_agreement.name_of_lead_org"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :error="getError('grant_agreement.name_of_lead_org')"
                                :data-vv-as="$t('medium_grant.technical__1_name_lead_org')"
                                :label="$t('medium_grant.technical__1_name_lead_org')"
                                :placeholder="$t('medium_grant.technical__1_name_lead_org')"
                                >
                            </textarea>
                        </div>

                        <!-- Reporting interval -->
                        <h4 class="card-title">{{ $t('monitoring.field_title_reporting_interval') }}</h4>
                        <div class="form-group">
                            <radio name="model.reporting_interval"
                                class="form-check-inline"
                                value="3"
                                v-model="MonitoringForm.model.frequency"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                >
                                <label>3 Months</label>
                            </radio>
                            <radio name="model.reporting_interval"
                                class="form-check-inline"
                                value="6"
                                v-model="MonitoringForm.model.frequency"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                >
                                <label>6 Months</label>
                            </radio>
                        </div>

                        <!-- Start & End date -->
                        <div class="row">
                            <!-- Starting date -->
                            <div class="col-lg-6">
                                <h4 class="card-title">{{ $t('medium_grant.technical__1_startdate') }}</h4>
                                <base-input
                                    name="grant_agreement.start_date"
                                    v-model="MonitoringForm.model.grant_agreement.start_date"
                                    :error="getError('grant_agreement.start_date')"
                                    v-validate="'required'"
                                    :data-vv-as="$t('medium_grant.technical__1_startdate')"
                                    :placeholder="$t('medium_grant.technical__1_startdate')"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <el-date-picker
                                        type="date"
                                        v-model="startDate"
                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                        :placeholder="$t('medium_grant.technical__1_startdate')"
                                        >
                                    </el-date-picker>
                                </base-input>
                            </div>

                            <!-- Ending date -->
                            <div class="col-lg-6">
                                <h4 class="card-title">{{ $t('medium_grant.technical__1_enddate') }}</h4>
                                <base-input
                                    name="grant_agreement.end_date"
                                    v-model="MonitoringForm.model.grant_agreement.end_date"
                                    :placeholder="$t('medium_grant.technical__1_enddate')"
                                    :error="getError('grant_agreement.end_date')"
                                    v-validate="'required'"
                                    :data-vv-as="$t('medium_grant.technical__1_enddate')"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <el-date-picker
                                        type="date"
                                        v-model="endDate"
                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                        :placeholder="$t('medium_grant.technical__1_enddate')"
                                        >
                                    </el-date-picker>
                                </base-input>
                            </div>
                        </div>

                        <!-- extended End date - by alan-->
                        <div class="row">
                            <!-- end date 1-->
                            <div class="col-lg-6" v-show="showDates1">
                                <h4 class="card-subtitle-new">a.{{ $t('monitoring.label_amended_date1') }}</h4>
                                <base-input
                                    name="grant_agreement.amendment_end_date_first"
                                    v-model="MonitoringForm.model.grant_agreement.amendment_end_date_first"
                                    :placeholder="$t('monitoring.label_amended_date1')"
                                    :error="getError2('grant_agreement.amendment_end_date_first')"
                                    v-validate="'required'"
                                    :data-vv-as="$t('monitoring.label_amended_date1')"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN || MonitoringForm.model.grant_agreement.end_date === null"
                                    >
                                    <el-date-picker
                                        type="date"
                                        v-model="amendEndDate1"
                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN || MonitoringForm.model.grant_agreement.end_date === null"
                                        :placeholder="$t('monitoring.label_amended_date1')"
                                        >
                                    </el-date-picker>
                                </base-input>
                            </div>

                            <!-- end date 2 -->
                            <div class="col-lg-6" v-show="showDates2">
                                <h4 class="card-subtitle-new">b.{{ $t('monitoring.label_amended_date2') }}</h4>
                                <base-input
                                    name="grant_agreement.amendment_end_date_second"
                                    v-model="MonitoringForm.model.grant_agreement.amendment_end_date_second"
                                    :placeholder="$t('monitoring.label_amended_date2')"
                                    :error="getError2('grant_agreement.amendment_end_date_second')"
                                    v-validate="'required'"
                                    :data-vv-as="$t('monitoring.label_amended_date2')"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN || MonitoringForm.model.grant_agreement.amendment_end_date_first === null"
                                    >
                                    <el-date-picker
                                        type="date"
                                        v-if="showAmendDate2"
                                        v-model="amendEndDate2"
                                        :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN || MonitoringForm.model.grant_agreement.amendment_end_date_first === null"
                                        :placeholder="$t('monitoring.label_amended_date2')"
                                        >
                                    </el-date-picker>
                                </base-input>
                            </div>
                        </div>

                        <!-- Duration -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('medium_grant.technical__1_duration') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.duration"
                                v-model="MonitoringForm.model.grant_agreement.duration"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :error="getError('grant_agreement.duration')"
                                :data-vv-as="$t('medium_grant.technical__1_duration')"
                                :label="$t('medium_grant.technical__1_duration')"
                                :placeholder="$t('medium_grant.technical__1_duration')"
                                >
                            </textarea>
                        </div>

                        <!-- Total budget -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('medium_grant.technical__1_total_budget') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.total_budget"
                                v-model="MonitoringForm.model.grant_agreement.total_budget"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :error="getError('grant_agreement.total_budget')"
                                :data-vv-as="$t('medium_grant.technical__1_total_budget')"
                                :label="$t('medium_grant.technical__1_total_budget')"
                                :placeholder="$t('medium_grant.technical__1_total_budget')"
                                >
                            </textarea>
                        </div>

                        <!-- Total funding requested -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('medium_grant.technical__1_total_funding') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.total_funding"
                                v-model="MonitoringForm.model.grant_agreement.total_funding"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :error="getError('grant_agreement.total_funding')"
                                :data-vv-as="$t('medium_grant.technical__1_total_funding')"
                                :label="$t('medium_grant.technical__1_total_funding')"
                                :placeholder="$t('medium_grant.technical__1_total_funding')"
                                >
                            </textarea>
                        </div>

                        <!-- Total Co-funding -->
                        <div class="form-group">
                            <h4 class="card-title">{{ $t('medium_grant.technical__1_total_cofunding') }}</h4>
                            <textarea
                                class="form-control"
                                name="grant_agreement.total_co_funding"
                                v-model="MonitoringForm.model.grant_agreement.total_co_funding"
                                v-validate="'required'"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                :error="getError('grant_agreement.total_co_funding')"
                                :data-vv-as="$t('medium_grant.technical__1_total_cofunding')"
                                :label="$t('medium_grant.technical__1_total_cofunding')"
                                :placeholder="$t('medium_grant.technical__1_total_cofunding')"
                                >
                            </textarea>
                        </div>

                    </div>

                    <!-- Reporting interval -->

                    <MonitoringGrantAgreementsQuestions
                        v-bind:loggedUser="loggedUser"
                        v-bind:UserRoles="UserRoles"
                        v-bind:MonitoringForm="MonitoringForm"
                    />

                    <!-- <div class="col-lg-6">

                        <h4 class="card-title">{{ $t('monitoring.field_title_keyword_for_better_identifying_your_project') }}</h4>
                        <h5 class="card-subtitle">{{ $t('monitoring.field_subtitle_type_of_priority_area') }}</h5>
                        <div class="form-group">
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.type_of_priority_keyword_marine_protected_conserved_area"
                                >
                                {{ $t('monitoring.field_type_of_priority_keyword_marine_protected_conserved_area') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.type_of_priority_keyword_marine_managed_areas"
                                >
                                {{ $t('monitoring.field_type_of_priority_keyword_marine_managed_areas') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.type_of_priority_keyword_terrestrial_protected_conserved_area"
                                >
                                {{ $t('monitoring.field_type_of_priority_keyword_terrestrial_protected_conserved_area') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.type_of_priority_keyword_indigenous_and_community_conserved_area"
                                >
                                {{ $t('monitoring.field_type_of_priority_keyword_indigenous_and_community_conserved_area') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.type_of_priority_keyword_privately_protected_area"
                                >
                                {{ $t('monitoring.field_type_of_priority_keyword_privately_protected_area') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.type_of_priority_keyword_conservancy"
                                >
                                {{ $t('monitoring.field_type_of_priority_keyword_conservancy') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.type_of_priority_keyword_key_landscape_for_conservation"
                                >
                                {{ $t('monitoring.field_type_of_priority_keyword_key_landscape_for_conservation') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.type_of_priority_keyword_kbas"
                                >
                                {{ $t('monitoring.field_type_of_priority_keyword_KBAs') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.type_of_priority_keyword_transboundary_areas"
                                >
                                {{ $t('monitoring.field_type_of_priority_keyword_transboundary_areas') }}
                            </base-checkbox>
                        </div>
                        <h5 class="card-subtitle">{{ $t('monitoring.field_subtitle_primary_objective') }}</h5>
                        <div class="form-group">
                            <radio name="model.frequency"
                                value="Governance"
                                v-model="MonitoringForm.model.grant_agreement.keyword_primary_objective"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                >
                                <label>{{ $t('monitoring.field_primary_objective_keyword_governance') }}</label>
                            </radio>
                            <radio name="model.frequency"
                                value="Management Effectiveness"
                                v-model="MonitoringForm.model.grant_agreement.keyword_primary_objective"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                >
                                <label>{{ $t('monitoring.field_primary_objective_keyword_management_effectiveness') }}</label>
                            </radio>
                            <radio name="model.frequency"
                                value="Livelihoods"
                                v-model="MonitoringForm.model.grant_agreement.keyword_primary_objective"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                >
                                <label>{{ $t('monitoring.field_primary_objective_keyword_livelihoods') }}</label>
                            </radio>
                        </div>
                        <h5 class="card-subtitle">{{ $t('monitoring.field_subtitle_target_groups') }}</h5>
                        <div class="form-group">
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.target_groups_keyword_local_communities"
                                >
                                {{ $t('monitoring.field_target_groups_keyword_local_communities') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.target_groups_keyword_indigenous_people"
                                >
                                {{ $t('monitoring.field_target_groups_keyword_indigenous_people') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.target_groups_keyword_youth"
                                >
                                {{ $t('monitoring.field_target_groups_keyword_youth') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.target_groups_keyword_women"
                                >
                                {{ $t('monitoring.field_target_groups_keyword_women') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.target_groups_keyword_private_sector"
                                >
                                {{ $t('monitoring.field_target_groups_keyword_private_sector') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.target_groups_keyword_farmers_fishermen"
                                >
                                {{ $t('monitoring.field_target_groups_keyword_farmers_fishermen') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.target_groups_keyword_national_local_pa_agency"
                                >
                                {{ $t('monitoring.field_target_groups_keyword_national_local_pa_agency') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.target_groups_keyword_national_government"
                                >
                                {{ $t('monitoring.field_target_groups_keyword_national_government') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.target_groups_keyword_subnational_governments"
                                >
                                {{ $t('monitoring.field_target_groups_keyword_subnational_governments') }}
                            </base-checkbox>
                        </div>
                        <h5 class="card-subtitle">{{ $t('monitoring.field_subtitle_main_activity_focus') }}</h5>
                        <div class="form-group row">
                            <div class="col-6">
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_management_plan"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_management_plan') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_enforcement"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_enforcement') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_legal_framework_regulation"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_legal_framework_regulation') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_illegal_activities"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_illegal_activities') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_co_management"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_co_management') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_participation"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_participation') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_livelihoods_alternative_economic_activities"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_livelihoods_alternative_economic_activities') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_sustainable_development"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_sustainable_development') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_landscape_approach"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_landscape_approach') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_eco_tourism"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_eco_tourism') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_sustainable_financing"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_sustainable_financing') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_threatened_species"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_threatened_species') }}
                                </base-checkbox>
                            </div>

                            <div class="col-6">
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_human_wildlife_conflict"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_human_wildlife_conflict') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_ecosystem_habitat_restoration"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_ecosystem_habitat_restoration') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_invasive_alien_species"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_invasive_alien_species') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_survey_data_knowledge_management"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_survey_data_knowledge_management') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_protected_area_management_effectiveness_pame"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_protected_area_management_effectiveness_pame') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_protected_area_governance_effectiveness_page"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_protected_area_governance_effectiveness_page') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_capacity_building"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_capacity_building') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_awareness_education"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_awareness_education') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_climate_adaptation_mitigation_resilience"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_climate_adaptation_mitigation_resilience') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_monitoring_patrol_equipment"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_monitoring_patrol_equipment') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_human_rights"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_human_rights') }}
                                </base-checkbox>
                                <base-checkbox
                                    name="checkbox"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                    v-model="MonitoringForm.model.grant_agreement.field_main_activity_focus_keyword_green_list"
                                    >
                                    {{ $t('monitoring.field_main_activity_focus_keyword_green_list') }}
                                </base-checkbox>
                            </div>
                        </div>

                        <h4 class="card-title">{{ $t('monitoring.field_title_support_to_local_communities') }}</h4>
                        <div class="form-group">
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_01"
                                >
                                {{ $t('monitoring.field_support_keyword_01') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_02"
                                >
                                {{ $t('monitoring.field_support_keyword_02') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_03"
                                >
                                {{ $t('monitoring.field_support_keyword_03') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_04"
                                >
                                {{ $t('monitoring.field_support_keyword_04') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_05"
                                >
                                {{ $t('monitoring.field_support_keyword_05') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_06"
                                >
                                {{ $t('monitoring.field_support_keyword_06') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_07"
                                >
                                {{ $t('monitoring.field_support_keyword_07') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_08"
                                >
                                {{ $t('monitoring.field_support_keyword_08') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_09"
                                >
                                {{ $t('monitoring.field_support_keyword_09') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_10"
                                >
                                {{ $t('monitoring.field_support_keyword_10') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_11"
                                >
                                {{ $t('monitoring.field_support_keyword_11') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_12"
                                >
                                {{ $t('monitoring.field_support_keyword_12') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_13"
                                >
                                {{ $t('monitoring.field_support_keyword_13') }}
                            </base-checkbox>
                            <base-checkbox
                                name="checkbox"
                                :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN && loggedUser.role !== UserRoles.APPLICANT"
                                v-model="MonitoringForm.model.grant_agreement.support_keyword_14"
                                >
                                {{ $t('monitoring.field_support_keyword_14') }}
                            </base-checkbox>
                        </div>

                    </div> -->
                </div>
            </card>
            <div class="row mb-5">
                <div class="col-12">
                    <div class="grant-agreement-box row mt-5">
                        <div class="col-12">
                            <div class="form-group file-group">
                                <span class="file-caption">{{ $t('monitoring.box_title__signed_contract') }}</span>
                                <upload-file
                                    v-if="MonitoringForm.initialized"
                                    v-model="MonitoringForm.files.grant_agreement.signed_contract"
                                    class="upload-file"
                                    section="grant_agreement.signed_contract"
                                    select-text="Admin Upload Contract"
                                    :file-data="MonitoringForm.files.grant_agreement.signed_contract"
                                    :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                    :model-name="'Monitoring'"
                                    :size-limit="MonitoringForm.fileSizeLimit"
                                >
                                </upload-file>
                            </div>
                        </div>
                    </div>

                    <div class="ammendment-box mt-3 row" v-for="(amend, index) in MonitoringForm.model.grant_agreement.contract_amends" :key="'contract_' + index">
                        <div class="col-12">
                            <div class="form-group file-group">
                                <span class="file-caption">{{ $t('monitoring.box_title__amend_request', [index + 1]) }}</span>
                                <upload-file
                                    v-if="MonitoringForm.initialized"
                                    v-model="MonitoringForm.files.grant_agreement['amend_' + index + '_signed_contract']"
                                    class="upload-file"
                                    :select-text="$t('monitoring.button_title_upload_ammended_contract')"
                                    :section="'grant_agreement.amend_' + index + '_signed_contract'"
                                    :file-data="MonitoringForm.files.grant_agreement['amend_' + index + '_signed_contract']"
                                    :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                    :model-name="'Monitoring'"
                                    :size-limit="MonitoringForm.fileSizeLimit"
                                >
                                </upload-file>
                            </div>
                            <div class="clearance-bar">
                                <label>{{ $t('monitoring.title_clearance_box') }}</label>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="cleared-with-condition"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_cleared_under_conditions') }}</label>
                                </radio>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="cleared"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_cleared') }}</label>
                                </radio>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="not-cleared"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_not_cleared') }}</label>
                                </radio>
                            </div>
                            <comment-box
                                class="form-group"
                                v-model="amend.comments"
                                :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                :comments="amend.comments"
                                :pull-right="false">
                            </comment-box>
                        </div>
                    </div>

                    <base-button
                        v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                        class="mt-4"
                        type="primary"
                        @click="addContractAmmendment"
                        >
                        {{ $t('monitoring.button_title_add_new_ammendment') }}
                    </base-button>
                </div>
            </div>
            <div class="row mb-5">
                <div class="col-12">
                    <div class="grant-agreement-box row mt-5">
                        <div class="col-12">
                            <div class="form-group file-group">
                                <span class="file-caption">{{ $t('monitoring.field_title_signed_budget') }}</span>
                                <upload-file
                                    v-if="MonitoringForm.initialized"
                                    v-model="MonitoringForm.files.grant_agreement.signed_budget"
                                    class="upload-file"
                                    section="grant_agreement.signed_budget"
                                    :select-text="$t('monitoring.button_title_admin_upload_budget')"
                                    :file-data="MonitoringForm.files.grant_agreement.signed_budget"
                                    :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                    :model-name="'Monitoring'"
                                    :size-limit="MonitoringForm.fileSizeLimit"
                                >
                                </upload-file>
                            </div>
                        </div>
                    </div>

                    <div class="ammendment-box mt-3 row" v-for="(amend, index) in MonitoringForm.model.grant_agreement.budget_amends" :key="'contract_' + index">
                        <div class="col-12">
                            <div class="form-group file-group">
                                <span class="file-caption">{{ $t('monitoring.box_title__amend_request', [index + 1]) }}</span>
                                <upload-file
                                    v-if="MonitoringForm.initialized"
                                    v-model="MonitoringForm.files.grant_agreement['amend_' + index + '_signed_budget']"
                                    class="upload-file"
                                    :select-text="$t('monitoring.button_title_upload_ammended')"
                                    :section="'grant_agreement.amend_' + index + '_signed_budget'"
                                    :file-data="MonitoringForm.files.grant_agreement['amend_' + index + '_signed_budget']"
                                    :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                    :model-name="'Monitoring'"
                                    :size-limit="MonitoringForm.fileSizeLimit"
                                >
                                </upload-file>
                            </div>
                            <div class="clearance-bar">
                                <label>{{ $t('monitoring.title_clearance_box') }}</label>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="cleared-with-condition"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_cleared_under_conditions') }}</label>
                                </radio>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="cleared"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_cleared') }}</label>
                                </radio>
                                <radio name="model.info_risk_assessment"
                                    class="form-check-inline"
                                    value="not-cleared"
                                    v-model="amend.clearance"
                                    :disabled="loggedUser.role !== UserRoles.ADMIN && loggedUser.role !== UserRoles.REGIONAL_ADMIN"
                                    >
                                    <label>{{ $t('monitoring.field_title_not_cleared') }}</label>
                                </radio>
                            </div>
                            <comment-box
                                class="form-group"
                                v-model="amend.comments"
                                :is-editable="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                                :comments="amend.comments"
                                :pull-right="false">
                            </comment-box>
                        </div>
                    </div>

                    <base-button
                        v-if="loggedUser.role === UserRoles.ADMIN || loggedUser.role === UserRoles.REGIONAL_ADMIN"
                        class="mt-4"
                        type="primary"
                        @click="addBudgetAmmendment"
                        >
                        {{ $t('monitoring.button_title_add_new_ammendment') }}
                    </base-button>
                </div>
            </div>
        </div>

        <div class="row mt-5" v-if="MonitoringForm.initialized && MonitoringForm.isEditable">
            <div class="col-12">
                <base-button type="default" wide @click.native="saveProgress">{{$t('general.save_progress')}}</base-button>
                <base-button class="pull-right" wide @click.native="submit">{{$t('general.submit')}}</base-button>
            </div>
        </div>
    </div>
</template>
<script>
    import {MonitoringForm, RegionService, UserRoles} from '@/services';
    import {DatePicker} from 'element-ui';
    import Multiselect from 'vue-multiselect';
    import {CommentBox} from 'src/components';
    import {EventBus} from '@/event-bus.js';

    import MonitoringGrantAgreementsQuestions from "../../MonitoringGrantAgreementsQuestions"

    export default
    {
        components:
        {
            MonitoringForm,
            [DatePicker.name]: DatePicker,
            Multiselect,
            CommentBox,
            MonitoringGrantAgreementsQuestions
        },
        data()
        {
            return {
                UserRoles,
                MonitoringForm,
                loggedUser: this.$store.getters['Auth/loggedUser'],
                showContractDetails: false,
                startDate: '',
                endDate: '',
                amendEndDate1: '',
                amendEndDate2: '',
                moment: '',
                regions: [],
                showAmendDate2: false,
                showAmendedDate1: true,
                showAmendedDate2: true

            }
        },
        computed:
        {
            countriesByRegion()
            {
                let countries = [];
                let region = this.regions.filter((region) =>
                {
                    return region.regionId === MonitoringForm.regionId;
                });

                if (region[0] !== undefined && region[0].countries !== undefined && region[0].countries.length)
                {
                    countries = region[0].countries;
                }

                return countries;
            },

            showDates1(){
                if(this.loggedUser.role == this.UserRoles.APPLICANT && this.MonitoringForm.model.grant_agreement.amendment_end_date_first == null){
                    this.showAmendedDate1 = false;
                    return this.showAmendedDate1;
                }
                else{
                    return this.showAmendedDate1;
                }
            },
            showDates2(){
                if(this.loggedUser.role == this.UserRoles.APPLICANT && this.MonitoringForm.model.grant_agreement.amendment_end_date_second == null){
                    this.showAmendedDate2 = false;
                    return this.showAmendedDate2;
                }
                else{
                    return this.showAmendedDate2;
                }
            }
        },
        async beforeMount()
        {
            const vm = this;
            const loader = vm.$loading.show();

            // vm.loggedUser = vm.$store.getters['Auth/loggedUser'];

            vm.moment = require('moment');

            RegionService.getApplicationRegionsWithCountries().then((response) =>
            {
                vm.regions = response.data;

                loader.hide();
            });

            if (!MonitoringForm.model.grant_agreement.contract_amends)
            {
                MonitoringForm.model.grant_agreement.contract_amends = [];
            }

            if (!MonitoringForm.model.grant_agreement.budget_amends)
            {
                MonitoringForm.model.grant_agreement.budget_amends = [];
            }

            vm.startDate = MonitoringForm.model.grant_agreement.start_date || '';
            vm.endDate = MonitoringForm.model.grant_agreement.end_date || '';
            //amend end date - by alan
            vm.amendEndDate1 = MonitoringForm.model.grant_agreement.amendment_end_date_first || '';
            vm.amendEndDate2 = MonitoringForm.model.grant_agreement.amendment_end_date_second || '';
        },
        async beforeCreate()
        {
            const vm = this;

            vm.loggedUser = vm.$store.getters['Auth/loggedUser'];

            vm.$forceUpdate();
        },
        methods:
        {
            addContractAmmendment()
            {
                MonitoringForm.model.grant_agreement.contract_amends.push({});

                this.$forceUpdate();
            },

            addBudgetAmmendment()
            {
                MonitoringForm.model.grant_agreement.budget_amends.push({});

                this.$forceUpdate();
            },

            getError(field)
            {
                return this.errors.first(field);
            },
            getError2(field)
            {
                if(this.errors.first(field)){
                    return 'Date should be greater than End Date';
                }
                else{
                    return this.errors.first(field);
                }
                
            },

            validate()
            {
                return this.$validator.validateAll().then(res =>
                {
                    this.$emit('on-validated', res, this.model);

                    return res;
                });
            },

            setRegion(ev)
            {
                MonitoringForm.regionId = parseInt(ev.target.value);
                MonitoringForm.model.grant_agreement.acp_country;
            },

            viewOriginalProposal() {
                // debugger;
                // window.open('/application/fill/' + MonitoringForm.grantId + '/' + MonitoringForm.applicationId, '_blank');
                if (MonitoringForm.cnEvaluationStatus == "Preselected") {
                    let routeData = this.$router.resolve({
                        name: "ConceptFill",
                        params: { grantId: MonitoringForm.grantId, applicationId: MonitoringForm.applicationId, conceptStatus: MonitoringForm.cnEvaluationStatus },
                    });
                    window.open(routeData.href, '_blank');

                }
                else {
                    let routeData = this.$router.resolve({
                        name: "Fill",
                        params: { grantId: MonitoringForm.grantId, applicationId: MonitoringForm.applicationId, appversion: MonitoringForm.newApplication },
                    });
                    window.open(routeData.href, '_blank');
                }
            },

            saveProgress()
            {
                EventBus.$emit('monitoring-save-progress');
            },

            submit()
            {
                EventBus.$emit('monitoring-submit');
            },
        },
        watch:
        {
            startDate(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement.start_date = (date === 'Invalid date' ? null : date);
            },
            endDate(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                MonitoringForm.model.grant_agreement.end_date = (date === 'Invalid date' ? null : date);
            },
            //Amend end date- by alan
            amendEndDate1(new_val)
            {
                const dt = MonitoringForm.model.grant_agreement.end_date;
                const date = this.moment(new_val).format('YYYY-MM-DD');
                if(date != 'Invalid date'){
                   this.showAmendDate2 = true;
                }
                //MonitoringForm.model.grant_agreement.amendment_end_date_1 = (date === 'Invalid date' ? null : date);
                MonitoringForm.model.grant_agreement.amendment_end_date_first = (date === 'Invalid date') ? null : (date > MonitoringForm.model.grant_agreement.end_date ? date : null);
            },
            amendEndDate2(new_val)
            {
                const date = this.moment(new_val).format('YYYY-MM-DD');
                //MonitoringForm.model.grant_agreement.amendment_end_date_2 = (date === 'Invalid date' ? null : date);
                MonitoringForm.model.grant_agreement.amendment_end_date_second = (date === 'Invalid date') ? null : (date > MonitoringForm.model.grant_agreement.amendment_end_date_first ? date : null);
            }
        }
    };
</script>
<style lang="scss" scoped>
.grant-agreement
{
    counter-reset: title_counter;
    // counter-reset: subtitle_counter;

    .card-title
    {
        counter-increment: title_counter;
        counter-reset: subtitle_counter;

        &:before
        {
            content: counter(title_counter) '. '
        }
    }

    .card-subtitle
    {
        margin: 10px 0;
        counter-increment: subtitle_counter;

        &:before
        {
            content: counter(title_counter) '.' counter(subtitle_counter) '. '
        }
    }
    .card-subtitle-new
    {
        counter-increment: alphabeticList;
        counter-reset:alphabeticList;

        &:before
        {
            content: counter(title_counter)
        }
    }
}
</style>
